/**
 * Sentry Bridge — SEG-PG-6.1 (2026-05-21).
 *
 * Thin helper that calls @sentry/nextjs SDK ONLY when DSN configured (runtime-gated).
 * Lets error-reporter.ts integrate with native Sentry SDK without hard dependency
 * on initialization succeeding.
 *
 * Why a bridge (not direct @sentry/nextjs import in error-reporter.ts):
 * - error-reporter.ts is universal (called from server-side too via SSR).
 * - @sentry/nextjs has split runtime (browser vs server vs edge).
 * - Bridge isolates the runtime detection + lazy import.
 * - Sentry SDK calls are no-op when init() didn't run (graceful, per Sentry docs).
 *
 * 跟 sentry.client.config.ts 关系: client config 已 init Sentry; bridge 仅 forward
 * additional captureException calls 到 already-initialized SDK.
 */

import * as Sentry from "@sentry/react";

/**
 * Check whether Sentry SDK has been initialized (DSN configured + init() ran).
 * Safe to call from any runtime.
 */
export function isSentryEnabled(): boolean {
  try {
    // Sentry.getClient() returns the active SentryClient if init() succeeded
    return Sentry.getClient() !== undefined;
  } catch {
    return false;
  }
}

/**
 * Capture an exception via Sentry SDK if enabled. Returns the event ID for
 * cross-reference with backend audit logs (跟 audit_logs HMAC trail 同模式).
 *
 * Graceful no-op when Sentry not initialized (DSN not set).
 */
export function captureExceptionGraceful(
  error: unknown,
  extras?: Record<string, unknown>,
): string | undefined {
  if (!isSentryEnabled()) return undefined;
  try {
    return Sentry.captureException(error, extras ? { extra: extras } : undefined);
  } catch {
    // Sentry SDK call failed — don't break the app, return undefined
    return undefined;
  }
}

/**
 * Capture a message (non-exception event) via Sentry SDK if enabled.
 * Useful for poor Web Vitals + 4xx API errors that aren't exceptions.
 */
export function captureMessageGraceful(
  message: string,
  level: "error" | "warning" | "info" = "info",
  extras?: Record<string, unknown>,
): string | undefined {
  if (!isSentryEnabled()) return undefined;
  try {
    return Sentry.captureMessage(message, {
      level,
      ...(extras ? { extra: extras } : {}),
    });
  } catch {
    return undefined;
  }
}

/**
 * Add a breadcrumb (visible in subsequent error reports — gives 上下文).
 * Useful for tracking user navigation, button clicks, API calls leading to error.
 */
export function addBreadcrumbGraceful(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  if (!isSentryEnabled()) return;
  try {
    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level: "info",
      timestamp: Date.now() / 1000,
    });
  } catch {
    // Silent
  }
}
